import Build, { CHARACTER_SEX } from '../core/Build';
import GenConfig, { normalizeDefenseShare } from '../core/GenConfig';
import { getCharacterClass } from '../core/characterUtils';
import { fmExosFromShortcuts } from '../core/itemMaging';

function normalizeReq(rawReq) {
    return rawReq && typeof rawReq === 'object' ? rawReq : {};
}

function normalizeMetadata(rawMetadata) {
    return rawMetadata && typeof rawMetadata === 'object' ? rawMetadata : {};
}

function applyStatsCriteria(baseBuild, rawStatsCriteria) {
    let build = baseBuild;
    const statsCriteria = rawStatsCriteria && typeof rawStatsCriteria === 'object'
        ? rawStatsCriteria
        : {};
    Object.entries(statsCriteria).forEach(([rawId, rawRule]) => {
        const statId = Number(rawId);
        if (!Number.isFinite(statId)) return;
        const rule = rawRule && typeof rawRule === 'object' ? rawRule : {};
        const minVal = Number(rule.min);
        if (Number.isFinite(minVal) && minVal > 0) {
            build = build.setDesired(statId, Math.trunc(minVal));
            return;
        }
        if (rule.maximize === true) {
            build = build.setMaximizeStat(statId, true);
        }
    });
    return build;
}

function normalizeSpellIds(rawSpellCriteria) {
    if (!Array.isArray(rawSpellCriteria)) return [];
    const seen = new Set();
    const out = [];
    rawSpellCriteria.forEach((entry) => {
        const spellId = Number(entry);
        if (!Number.isFinite(spellId)) return;
        if (seen.has(spellId)) return;
        seen.add(spellId);
        out.push(spellId);
    });
    return out.sort((a, b) => a - b);
}

function normalizeSpellCriteria(rawSpellCriteria) {
    if (!Array.isArray(rawSpellCriteria)) return [];
    const seen = new Set();
    const out = [];
    rawSpellCriteria.forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;
        const spellId = Number(entry.spell_id ?? entry.spellId ?? entry.id);
        if (!Number.isFinite(spellId)) return;
        const rawKeys = Array.isArray(entry.line_keys)
            ? entry.line_keys
            : (Array.isArray(entry.lineKeys) ? entry.lineKeys : []);
        const lineKeys = Array.from(new Set(
            rawKeys.map((key) => String(key || '').trim()).filter(Boolean)
        )).sort();
        if (!lineKeys.length) return;
        const sig = `${spellId}:${lineKeys.join('||')}`;
        if (seen.has(sig)) return;
        seen.add(sig);
        out.push({ spell_id: spellId, line_keys: lineKeys });
    });
    return out.sort((a, b) => {
        if (a.spell_id !== b.spell_id) return a.spell_id - b.spell_id;
        return a.line_keys.join(',').localeCompare(b.line_keys.join(','));
    });
}

export function aiDraftToBuild({ req: rawReq, draft_metadata: rawMetadata }) {
    const req = normalizeReq(rawReq);
    const metadata = normalizeMetadata(rawMetadata);
    const classSlug = typeof metadata.characterClass === 'string'
        ? metadata.characterClass
        : 'iop';
    const characterClass = getCharacterClass(classSlug) || getCharacterClass('iop');

    let build = new Build({
        lvl: Number(req.lvl) || 200,
        items: Array(16).fill(null),
        characterClass,
        // The draft's exo toggles are the exos its generation places.
        fmExos: fmExosFromShortcuts(req.exos),
        maximizeSpellIds: normalizeSpellIds(req.spells_criteria),
        maximizeSpellCriteria: normalizeSpellCriteria(req.spells_criteria),
        maximizeWeapon: false,
    });

    const sex = Number(metadata.characterSex);
    if (sex === CHARACTER_SEX.FEMALE || sex === CHARACTER_SEX.MALE) {
        build = build.setCharacterSex(sex);
    }

    build = applyStatsCriteria(build, req.stats_criteria);

    const rawTimeLimit = Number(req.time_limit);
    const genConfig = new GenConfig({
        defenseShare: normalizeDefenseShare(req.defense_share),
        ...(Number.isFinite(rawTimeLimit) && rawTimeLimit > 0 ? { timeLimit: rawTimeLimit } : {}),
    });
    return { build, genConfig };
}

